(ns common.resource.query.adset
  (:require [camel-snake-kebab.core :as csk]
            [camel-snake-kebab.extras :as cske]
            [cljs-bean.core :refer [->clj bean]]
            [clojure.string :as str]
            [common.content-function.core :as cfn]
            [common.db.effects.http :refer [jsonify]]
            [common.queries :refer [invalidate-queries
                                    prevent-client-error-retries query-client]]
            [common.queries.http :as qhttp]
            [common.util :as util]
            [promesa.core :as p]
            [re-frame.core :as rf]))

(def entity-name "adset")

(defn transform-stats [^js data]
  (->> (->clj data)
       (mapv (fn [{:keys [adset-id impressions clicks]}]
               {:id adset-id
                :impressions impressions
                :clicks clicks
                :ctr (/ clicks impressions)}))))

(defn adsets-query [{:keys [advertiser-id campaign-id]}]
  #js {:queryKey #js ["adsets" campaign-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/adsets {}
                                           {:filters (jsonify
                                                      (cond-> {}
                                                        campaign-id (assoc :campaignId campaign-id)
                                                        advertiser-id (assoc :advertiserId advertiser-id)))
                                            :embed (jsonify ["template_revisions"])})))
       :staleTime (* 60 1000)})

(defn adsets-stats-query [{:keys [campaign-id daterange]}]
  #js {:queryKey #js ["adsets-stats" campaign-id daterange]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :measure-api/adset-stats {}
                                           (->> (merge daterange
                                                       {:campaign-id campaign-id})
                                                (cske/transform-keys csk/->snake_case)))))
       :select transform-stats
       :staleTime (* 60 1000)})

(defn adsets-warnings-query [{:keys [advertiser-id adset-ids]}]
  #js {:queryKey #js ["adsets-warnings" adset-ids]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/adsets-warnings {}
                                           {:advertiser-id advertiser-id
                                            :adset-ids (str/join "," adset-ids)})))
       :enabled (boolean (seq adset-ids))
       :staleTime (* 60 1000)})

(defn adset-query [{:keys [adset-id]}]
  #js {:queryKey #js ["adset" adset-id]
       :enabled (boolean adset-id)
       :queryFn (fn [_ctx] (qhttp/get (qhttp/render :api/adset {:adset-id adset-id}
                                                    {:embed (jsonify ["template_revisions"])})))
       :select (fn [^js data]
                 (let [{:keys [templateRevisions] :as data} (->clj data)]
                   (assoc data :templateRevisionIds (mapv :id templateRevisions))))
       :staleTime (* 60 1000)})

(def ensure-adset-controller
  {:parameters {:path [:adset-id]}
   :start (fn [{path-params :path}]
            (-> (p/let [data (.ensureQueryData query-client (adset-query path-params))
                        adset (->clj data)]
                  (rf/dispatch [:resource.adset/get-one-success adset]))
                (p/catch #(rf/dispatch [:resource.adset/get-one-failure]))))
   :stop #(rf/dispatch [:layer.adset/destroy])})

(defn create-adset-mutation [{:keys [campaign-id]}]
  #js {:mutationKey #js ["create-adset" campaign-id]
       :mutationFn (fn [adset]
                     (qhttp/post (qhttp/render :api/adsets {})
                                 {:body (assoc adset :campaignId campaign-id)}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSettled (fn []
                    (rf/dispatch [:loader/close]))
       :onSuccess (fn []
                    (invalidate-queries {:queryKey ["campaign" campaign-id]})
                    (invalidate-queries {:queryKey ["adsets" campaign-id]})
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name entity-name
                                   :type :success
                                   :message "Successfully created adset"}]))
       :onError (fn []
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to create adset"}]))})

(defn patch-adset-mutation [{:keys [adset-id]}]
  #js {:mutationKey #js ["patch-adset" adset-id]
       :mutationFn (fn [payload]
                     (qhttp/patch (qhttp/render :api/adset {:adset-id adset-id})
                                  {:body payload}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSettled (fn []
                    (rf/dispatch [:loader/close]))
       :onSuccess (fn [response]
                    (let [adset (->clj response)]
                      (invalidate-queries {:queryKey ["adsets" (:campaignId adset)]})
                      (invalidate-queries {:queryKey ["adset" adset-id]})
                      (rf/dispatch [:response-handler/handle-response
                                    {:entity-name entity-name
                                     :type :success
                                     :message "Successfully saved adset"}])))
       :onError (fn [error]
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to save adset"
                                 :response (->clj (:data error))}]))})

(defn then-add-google-cm-data-source [^js adset-mutation {:keys [advertiser-id]}]
  ;; Patches the onSuccess to have some code running
  ;; after than the original function returns.
  (let [on-success (.-onSuccess adset-mutation)]
    (set! (.-onSuccess adset-mutation)
          (fn [& [data :as args]]
            ;; The original onSuccess function.
            (apply on-success args)

            ;; The extra code to run.
            (let [{workflow :workflow
                   adset-id :id} (bean data)]
              (when (= workflow "rules")
                (let [content-function {:meta {:advertiser-id advertiser-id
                                               :schema "urn:lemonpi:schema:content-function:rules:v1"}
                                        :data  {:placeholders []
                                                :rules [{:assignments []
                                                         :predicate true}]
                                                :context [{:alias "google-cm"
                                                           :schema "urn:lemonpi:schema:context:google-cm:v1"}]}}]
                  (rf/dispatch [:resource.content-function/put-one
                                (cfn/->payload content-function)
                                {:adset-id adset-id
                                 :stage "draft"}])))))))

  ;; Returns the mutated JS object.
  adset-mutation)

(defn delete-adset-mutation [{:keys [campaign-id]}]
  #js {:mutationKey #js ["delete-adset"]
       :mutationFn (fn [{:keys [id]}]
                     (qhttp/delete (qhttp/render :api/adset {:adset-id id})))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSettled (fn []
                    (rf/dispatch [:loader/close]))
       :onSuccess (fn [_]
                    (invalidate-queries {:queryKey ["adsets" campaign-id]})
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name entity-name
                                   :type :success
                                   :message "Successfully deleted adset"}]))
       :onError (fn [response]
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to delete adset"
                                 :response response}]))})

(defn duplicate-adset-mutation [{:keys [campaign-id adset-id]}]
  #js {:mutationKey #js ["duplicate-adset" adset-id]
       :mutationFn (fn [{:keys [duplicate] :as params}]
                     (qhttp/post (qhttp/render :api/adset-duplicate {:adset-id adset-id}
                                               (cond-> {}
                                                 (some? duplicate) (assoc :duplicate_cfn (name duplicate))))
                                 {:body (dissoc params :duplicate)}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSettled (fn []
                    (rf/dispatch [:loader/close]))
       :onSuccess (fn []
                    (invalidate-queries {:queryKey ["adsets" campaign-id]})
                    (rf/dispatch [:response-handler/handle-response
                                  {:entity-name entity-name
                                   :type :success
                                   :message "Successfully duplicated adset"}]))
       :onError (fn [response]
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to duplicate adset"
                                 :response response}]))})

(defn adset-exports-history-query [{:keys [adset-id]}]
  #js {:queryKey #js ["adset-exports-history" adset-id]
       :queryFn (fn [_ctx] (qhttp/get (qhttp/render :api/adset-exports-history {:adset-id adset-id})))
       :enabled (boolean adset-id)
       :staleTime (* 60 1000)})

(defn adset-template-query [{:keys [adset-id]}]
  #js {:queryKey #js ["adset-template" adset-id]
       :queryFn (fn [_ctx] (qhttp/get (qhttp/render :api/adset-template {:adset-id adset-id})))
       :enabled (boolean adset-id)
       :staleTime (* 60 1000)})

(defn adset-aco-active-configuration [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["adset-aco-active-configuration" advertiser-id adset-id]
       :queryFn (fn [_ctx] (qhttp/get (qhttp/render :api/adset-aco-configuration-active
                                                    {:advertiser-id advertiser-id
                                                     :adset-id adset-id})))
       :enabled (boolean adset-id)
       :staleTime (* 60 1000)
       :retry prevent-client-error-retries})

(defn- select-preflight-score [^js data]
  (when-let [score (some-> data
                           ->clj
                           (as-> response (or (:latest-draft response) (:active response))))]
    (update score :recommendations #(or % []))))

(defn adset-aco-preflight-score
  [{:keys [advertiser-id adset-id vertical optimization-target]}]
  #js {:queryKey #js ["adset-aco-preflight-score" advertiser-id adset-id vertical optimization-target]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/adset-aco-preflight-score
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id}
                                           (util/filter-nils-map
                                            {:vertical (util/keyword-name vertical)
                                             :optimization-target (util/keyword-name optimization-target)}))))
       :select select-preflight-score
       :enabled (boolean adset-id)
       :staleTime (* 60 1000)
       :retry prevent-client-error-retries})

(defn put-template-mutation [{:keys [campaign-id adset-id]}]
  #js {:mutationKey #js ["adset-template-put" adset-id]
       :mutationFn (fn [payload]
                     (qhttp/put (qhttp/render :api/adset-template {:adset-id adset-id})
                                {:body payload}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSettled (fn []
                    (rf/dispatch [:loader/close]))
       :onSuccess (fn []
                    (let [{feed-id :feedId} (->clj (.getQueryData query-client #js ["adset" adset-id]))]
                      (rf/dispatch [:response-handler/handle-response
                                    {:entity-name "template"
                                     :type :success
                                     :message (cond-> "Successfully updated templates."
                                                feed-id (str " The content is being rendered using the new template."))}])
                      (invalidate-queries {:queryKey ["campaign" campaign-id]})
                      (invalidate-queries {:queryKey ["adset" adset-id]})
                      (invalidate-queries {:queryKey ["adset-template" adset-id]})
                      (rf/dispatch [:resource.template/upgrade-hints])))
       :onError (fn [response]
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name "template"
                                 :type :error
                                 :message "Failed to update templates"
                                 :response response}]))})

(defn append-templates-to-adset
  "Appends new templates to an existing adset's template list.
   Takes existing templates from adset query data and new templates to add.
   Returns the updated template list for the `put-template-mutation`"
  [adset new-templates]
  (let [existing-templates (:templateRevisions adset)
        new-template-revisions (map :latestRevision new-templates)
        adset-templates (->> (concat existing-templates new-template-revisions)
                             (map #(select-keys % [:version :templateId])))]
    adset-templates))

(defn remove-template-from-adset
  "Removes a template from an adset's template list.
   Takes existing templates from adset query data and template ID to remove.
   Returns the updated template list for the `put-template-mutation`"
  [adset template-id]
  (let [existing-templates (:templateRevisions adset)
        adset-templates (->> (remove #(= (:templateId %) template-id) existing-templates)
                             (map #(select-keys % [:version :templateId])))]
    adset-templates))
